import styled from '@emotion/styled';
import { primaryFontFamily } from '../../../styles/fonts';
import { minDeviceWidth } from '../../../styles/responsive';
import { AttachedFileProps } from '../../Contact/utils/upload.types';
import Input from '../Input';
import { ReactComponent as CheckSvg } from './icons/check.svg';
import { ReactComponent as CloseSvg } from './icons/close.svg';

export const UploadButton = styled.div<{ fluid: boolean; isDrag: boolean }>`
  ${primaryFontFamily}
  width: ${({ fluid }) => (fluid ? '100%' : 'auto')};
  background-color: ${({ theme, isDrag }) =>
    isDrag ? theme.default.colors.n1 : theme.default.colors.n05};
  border-radius: 4px;
  border: 1px dashed
    ${({ theme, isDrag }) =>
      isDrag ? theme.default.colors.n3 : theme.default.colors.n2};
  padding: ${({ theme }) => theme.spacing(4)};
  display: flex;
  align-items: center;
  justify-content: center;
  column-gap: ${({ theme }) => theme.spacing(2)};
  cursor: pointer;

  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
`;

export const Wrapper = styled.div`
  ${primaryFontFamily}
  display: flex;
  flex-direction: column;
  row-gap: ${({ theme }) => theme.spacing(4)};
  margin-top: ${({ theme }) => theme.spacing(4)};
`;

export const UploadWrapper = styled.div<{ isError: boolean }>`
  ${primaryFontFamily}
  position: relative;
  border-radius: ${({ theme }) => theme.spacing(1)};
  border: ${({ theme, isError }) =>
    isError && `1px solid ${theme.default.colors.red}`};
  overflow: hidden;
`;

export const UploadedFile = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(4)};
  padding: ${({ theme }) => theme.spacing(4)};
  background-color: ${({ theme }) => theme.default.colors.n05};
  color: ${({ theme }) => theme.default.colors.n5};
`;

export const UploadedFileContent = styled.div`
  flex-grow: 1;
  flex-shrink: 1;
  display: block;
  column-gap: 16px;
  overflow: hidden;

  @media ${minDeviceWidth('tablet')} {
    display: flex;
  }
`;

export const UploadedFileName = styled.div`
  flex-shrink: 1;
  flex-grow: 1;
  font-weight: 500;
  font-size: ${({ theme }) => theme.spacing(4)};
  line-height: ${({ theme }) => theme.spacing(6)};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const UploadStatusMessage = styled.div<{
  status: AttachedFileProps['status'];
}>`
  display: ${({ status }) => (status !== 'pending' ? 'flex' : 'none')};
  align-items: center;
  flex-shrink: 0;
  column-gap: ${({ theme }) => theme.spacing(1)};
  font-weight: 400;
  font-size: 14px;
  height: 24px;
  color: ${({ theme, status }) =>
    status === 'error' ? theme.default.colors.red : theme.default.colors.n5};
`;

export const StyledCloseSvg = styled(CloseSvg)`
  cursor: pointer;
  flex-shrink: 0;
`;

export const StyledCheckSvg = styled(CheckSvg)`
  fill: ${({ theme }) =>
    theme.default.colors.Feedback.Variations.Success.Success4};
`;

export const ProgressBar = styled.div`
  width: 100%;
  height: 2px;
  position: absolute;
  bottom: 0;
`;

export const ProgressValue = styled.div<{ percentage: number }>`
  width: ${({ percentage }) => percentage}%;
  background-color: ${({ theme, percentage }) =>
    percentage === 100
      ? theme.default.colors.Feedback.Variations.Success.Success4
      : theme.default.primaryColor};
  height: 2px;
  transition: width 0.3s ease-in-out, background-color 0.3s ease-in-out;
`;

export const HiddenFileInput = styled(Input)`
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;

export const FileInfo = styled.div`
  display: flex;
  padding-top: ${({ theme }) => theme.spacing(2)};
  font-size: 14px;
  font-weight: 400;
  color: ${({ theme }) => theme.default.colors.n3};
`;
